// 編輯指令：把使用者動作轉成 applyOps 操作。
import { OpError, parseOutline, serializeOutline, type MapOp, type MindMap, type Topic, type TopicInput, type TopicAttrs } from '@sind/core';
import type { EditorState } from './state.js';
import type { Canvas, DropPosition } from './canvas.js';
import { t } from './i18n.js';

export type Direction = 'left' | 'right' | 'up' | 'down';

export class Commands {
  constructor(private readonly state: EditorState, private readonly canvas: Canvas, private readonly notify: (message: string) => void) {}

  // 套用操作；OpError 轉成提示，不讓例外中斷介面。
  run(ops: MapOp[], select?: (created: string[]) => EditorState['selection']): string[] | null {
    try {
      return this.state.apply(ops, select);
    } catch (error) {
      if (error instanceof OpError) { this.notify(error.message); return null; }
      throw error;
    }
  }

  private selectedId(): string | null {
    const selection = this.state.selection;
    if (selection?.kind !== 'topic') { this.notify(t('toast.nothingSelected')); return null; }
    return selection.id;
  }

  private isRoot(id: string): boolean {
    return id === this.state.map.root.id;
  }

  // 新增後立即進入文字編輯（XMind 行為）。
  private addAndEdit(...ops: MapOp[]): void {
    const created = this.run(ops, ids => ({ kind: 'topic', id: ids[0]! }));
    if (created?.[0]) { this.canvas.reveal(created[0]); this.canvas.startEdit(created[0]); }
  }

  // 收合的主題先展開再新增，兩步算同一次操作（復原一次即可）。
  addChild(id = this.selectedId()): void {
    if (!id) return;
    const add: MapOp = { op: 'add', parent: id, title: t('default.child') };
    if (findTopic(this.state.map.root, id)?.collapsed) this.addAndEdit({ op: 'update', id, attrs: { collapsed: false } }, add);
    else this.addAndEdit(add);
  }

  addSibling(before = false, id = this.selectedId()): void {
    if (!id) return;
    if (this.isRoot(id)) { this.addChild(id); return; }
    this.addAndEdit(before ? { op: 'add', before: id, title: t('default.sibling') } : { op: 'add', after: id, title: t('default.sibling') });
  }

  insertParent(): void {
    const id = this.selectedId();
    if (!id) return;
    if (this.isRoot(id)) { this.notify(t('toast.rootOnly')); return; }
    this.addAndEdit({ op: 'insert_parent', id, title: t('default.parent') });
  }

  // 刪除後選取下一個兄弟、前一個兄弟或父主題。
  deleteSelection(): void {
    const selection = this.state.selection;
    if (selection?.kind === 'relation') { this.run([{ op: 'unrelate', from: selection.from, to: selection.to }], () => null); return; }
    const id = this.selectedId();
    if (!id) return;
    if (this.isRoot(id)) { this.notify(t('toast.rootOnly')); return; }
    const parent = this.state.parentOf(id)!;
    const index = parent.children.findIndex(topic => topic.id === id);
    const next = parent.children[index + 1] ?? parent.children[index - 1] ?? parent;
    this.run([{ op: 'delete', id }], () => ({ kind: 'topic', id: next.id }));
  }

  reorder(direction: -1 | 1): void {
    const id = this.selectedId();
    if (!id || this.isRoot(id)) return;
    const parent = this.state.parentOf(id)!;
    const index = parent.children.findIndex(topic => topic.id === id);
    const neighbour = parent.children[index + direction];
    if (!neighbour) return;
    this.run([direction < 0 ? { op: 'move', id, before: neighbour.id } : { op: 'move', id, after: neighbour.id }], () => ({ kind: 'topic', id }));
  }

  toggleCollapse(id = this.selectedId()): void {
    if (!id) return;
    const topic = findTopic(this.state.map.root, id);
    if (!topic || (!topic.children.length && !topic.collapsed)) return;
    this.run([{ op: 'update', id, attrs: { collapsed: !topic.collapsed } }], () => ({ kind: 'topic', id }));
  }

  toggleBoundary(): void {
    const id = this.selectedId();
    if (!id) return;
    if (this.isRoot(id)) { this.notify(t('toast.rootOnly')); return; }
    const topic = findTopic(this.state.map.root, id)!;
    this.run([{ op: 'update', id, attrs: { boundary: topic.boundary === undefined ? '' : null } }], () => ({ kind: 'topic', id }));
  }

  setAttrs(id: string, attrs: TopicAttrs, title?: string): void {
    this.run([{ op: 'update', id, attrs, ...(title !== undefined ? { title } : {}) }], () => ({ kind: 'topic', id }));
  }

  rename(id: string, title: string): void {
    const topic = findTopic(this.state.map.root, id);
    if (!topic || topic.title === title) return;
    this.run([{ op: 'update', id, title }], () => ({ kind: 'topic', id }));
  }

  move(id: string, target: string, position: DropPosition): void {
    const op: MapOp = position === 'child' ? { op: 'move', id, parent: target } : position === 'before' ? { op: 'move', id, before: target } : { op: 'move', id, after: target };
    this.run([op], () => ({ kind: 'topic', id }));
  }

  relate(from: string, to: string): void {
    this.run([{ op: 'relate', from, to }], () => ({ kind: 'relation', from, to }));
  }

  setRelationLabel(from: string, to: string, label: string): void {
    this.run([{ op: 'relate', from, to, ...(label ? { label } : {}) }], () => ({ kind: 'relation', from, to }));
  }

  // 方向鍵：在該方向上找最近的主題（主軸距離 + 2 倍副軸距離）。
  navigate(direction: Direction): void {
    const selection = this.state.selection;
    const current = selection?.kind === 'topic' ? this.canvas.node(selection.id) : this.canvas.node(this.state.map.root.id);
    if (!current) return;
    const cx = current.x + current.w / 2;
    const cy = current.y + current.h / 2;
    let best: { id: string; score: number } | null = null;
    for (const node of this.canvas.layout.nodes) {
      if (node.id === current.id) continue;
      const dx = node.x + node.w / 2 - cx;
      const dy = node.y + node.h / 2 - cy;
      const [primary, secondary] = direction === 'left' ? [-dx, dy] : direction === 'right' ? [dx, dy] : direction === 'up' ? [-dy, dx] : [dy, dx];
      if (primary <= 1) continue;
      const score = primary + Math.abs(secondary) * 2;
      if (!best || score < best.score) best = { id: node.id, score };
    }
    if (best) { this.state.select({ kind: 'topic', id: best.id }); this.canvas.reveal(best.id); }
  }

  // 子樹 → Sind 大綱文字（可直接貼給 Claude 或貼回編輯器）。
  copyText(): string | null {
    const topic = this.state.selectedTopic;
    if (!topic) return null;
    const map: MindMap = { root: topic, relations: [], structure: this.state.map.structure, theme: this.state.map.theme };
    return serializeOutline(map, { ids: 'none' });
  }

  cut(): string | null {
    const text = this.copyText();
    if (text && this.state.selection?.kind === 'topic' && !this.isRoot(this.state.selection.id)) this.deleteSelection();
    return text;
  }

  // 貼上：大綱文字 → 子主題。有 # 標題時整棵當一個主題，否則每個頂層項目各成一個主題；純文字逐行成主題。
  paste(text: string, parentId = this.selectedId()): boolean {
    if (!parentId) return false;
    const topics = textToTopics(text);
    if (!topics.length) { this.notify(t('error.parse')); return false; }
    const created = this.run([{ op: 'graft', parent: parentId, topics }], ids => ({ kind: 'topic', id: ids[0]! }));
    if (created?.[0]) this.canvas.reveal(created[0]);
    return Boolean(created);
  }
}

// 從 Claude 回覆中取出大綱：有 ``` 區塊就取第一個像大綱的區塊。
export function extractOutline(text: string): string {
  const blocks = [...text.matchAll(/```[\w-]*\r?\n([\s\S]*?)```/g)].map(match => match[1]!);
  return blocks.find(block => /^\s*(#|[-*+]\s|\d+[.)]\s)/m.test(block)) ?? text;
}

// 任意文字 → 主題樹輸入。
export function textToTopics(text: string): TopicInput[] {
  const outline = extractOutline(text).trim();
  if (!outline) return [];
  const hasHeading = /^#\s/m.test(outline);
  const hasBullets = /^\s*([-*+]|\d+[.)])\s/m.test(outline);
  if (!hasHeading && !hasBullets) {
    return outline.split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(title => ({ title }));
  }
  const source = hasHeading ? outline : `# __paste__\n${outline}`;
  const { map } = parseOutline(source);
  if (!map) return [];
  const convert = (topic: Topic): TopicInput => ({
    title: topic.title,
    attrs: {
      ...(topic.icon ? { icon: topic.icon } : {}),
      ...(topic.color ? { color: topic.color } : {}),
      ...(topic.boundary !== undefined ? { boundary: topic.boundary } : {}),
      ...(topic.collapsed ? { collapsed: true } : {}),
      ...(topic.note ? { note: topic.note } : {}),
    },
    children: topic.children.map(convert),
  });
  return hasHeading ? [convert(map.root)] : map.root.children.map(convert);
}

export function findTopic(root: Topic, id: string): Topic | null {
  if (root.id === id) return root;
  for (const child of root.children) {
    const found = findTopic(child, id);
    if (found) return found;
  }
  return null;
}
